/**
 * A small "are you sure?" modal, in the console's modal styling (the scrim,
 * white pane and modal shadow of `ConnectedDataSettings` / `ProjectDetails`).
 *
 * First used by Walk photos' deletes on an unlinked project — the phone's
 * `WalkPhotoDeleteDialog`: a title, a question, a consequence line, Cancel and
 * a red confirm. Escape and a click on the scrim cancel, except while the
 * confirmed action is running.
 *
 * Portaled, and above the photo viewer (z-index 1000), so it can be raised
 * from inside it.
 */

import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { DS, FONT_TEXT, RT, WPC } from "../tokens";

export interface ConfirmDialogProps {
  title: string;
  message: ReactNode;
  detail?: ReactNode;
  confirmLabel: string;
  busyLabel?: string;
  cancelLabel?: string;
  busy?: boolean;
  /** Red confirm button (a delete). */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  testId?: string;
}

export function ConfirmDialog({
  title,
  message,
  detail,
  confirmLabel,
  busyLabel,
  cancelLabel = "Cancel",
  busy = false,
  destructive = false,
  onConfirm,
  onCancel,
  testId = "confirm-dialog",
}: ConfirmDialogProps) {
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      // The viewer under this dialog listens for Escape too: this one wins.
      event.stopImmediatePropagation();
      event.preventDefault();
      if (!busyRef.current) onCancel();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  return createPortal(
    <div
      data-testid={`${testId}-scrim`}
      onClick={() => { if (!busy) onCancel(); }}
      style={{ position: "fixed", inset: 0, zIndex: 1100, background: RT.scrim, display: "flex", alignItems: "center", justifyContent: "center", padding: 16, fontFamily: FONT_TEXT }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy}
        data-testid={testId}
        onClick={(event) => event.stopPropagation()}
        style={{ width: 420, maxWidth: "100%", background: RT.pane, borderRadius: 16, boxShadow: RT.shadowModal, overflow: "hidden", color: DS.ink }}
      >
        <div style={{ padding: "22px 24px 0" }}>
          <div id={titleId} style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-.4px" }}>{title}</div>
          <div data-testid={`${testId}-message`} style={{ fontSize: 14.5, lineHeight: 1.5, marginTop: 8 }}>{message}</div>
          {detail ? <div style={{ fontSize: 13, lineHeight: 1.5, color: DS.body2, marginTop: 6 }}>{detail}</div> : null}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "20px 24px 20px" }}>
          <button
            ref={cancelRef}
            type="button"
            data-testid={`${testId}-cancel`}
            disabled={busy}
            onClick={onCancel}
            style={{ height: 40, padding: "0 16px", borderRadius: 20, border: "1px solid rgba(10,10,10,.14)", background: RT.pane, font: "inherit", fontSize: 14, fontWeight: 700, color: DS.ink, cursor: busy ? "default" : "pointer" }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            data-testid={`${testId}-confirm`}
            disabled={busy}
            onClick={onConfirm}
            style={{ height: 40, padding: "0 18px", borderRadius: 20, border: "none", background: destructive ? WPC.red : DS.ink, font: "inherit", fontSize: 14, fontWeight: 700, color: WPC.white, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}
          >
            {busy ? busyLabel ?? confirmLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
